import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/contexts/AuthContext';
import {
  LayoutDashboard, Wallet, TrendingUp, Users, ArrowDownCircle, ArrowUpCircle,
  Bell, User, LogOut, Menu, X, Shield, FileText, Home, ChevronLeft
} from 'lucide-react';

interface NavItem { path: string; label: string; icon: any; group?: string; }

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
  { path: '/wallet', label: 'Wallet', icon: Wallet, group: 'Finance' },
  { path: '/investments', label: 'Investments', icon: TrendingUp, group: 'Finance' },
  { path: '/investments/plans', label: 'Plans', icon: FileText, group: 'Finance' },
  { path: '/earnings', label: 'Earnings', icon: TrendingUp, group: 'Income' },
  { path: '/deposit', label: 'Deposit', icon: ArrowDownCircle, group: 'Finance' },
  { path: '/withdraw', label: 'Withdraw', icon: ArrowUpCircle, group: 'Finance' },
  { path: '/team', label: 'My Team', icon: Users, group: 'Network' },
  { path: '/kyc', label: 'KYC', icon: Shield, group: 'Account' },
  { path: '/profile', label: 'Profile', icon: User, group: 'Account' },
  { path: '/notifications', label: 'Notifications', icon: Bell, group: 'Account' },
];

export default function UserLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const grouped = navItems.reduce<Record<string, NavItem[]>>((acc, item) => {
    const g = item.group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

  return (
    <div className="flex h-screen bg-fintech-900 text-white overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-fintech-950 border-r border-white/[0.06] transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-64'} ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-white/[0.06] shrink-0 ${collapsed ? 'justify-center px-2' : 'px-4 justify-between'}`}>
          {!collapsed && (
            <Link to="/dashboard" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center">
                <span className="text-white font-bold text-xs">GR</span>
              </div>
              <span className="font-bold text-sm text-white tracking-tight">GR Trading</span>
            </Link>
          )}
          {collapsed && (
            <Link to="/dashboard" className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center">
              <span className="text-white font-bold text-xs">GR</span>
            </Link>
          )}
          <button onClick={() => collapsed ? setCollapsed(false) : setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 scrollbar-none">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="mb-1">
              {!collapsed && <div className="d-nav-group">{group}</div>}
              {items.map(item => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                    className={`${active ? 'd-nav-item-active' : 'd-nav-item-default'} ${collapsed ? 'justify-center px-2' : ''}`}
                    title={collapsed ? item.label : undefined}>
                    <Icon className="w-[18px] h-[18px] shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Collapse toggle (desktop) */}
        <div className="hidden lg:flex items-center justify-center py-2 border-t border-white/[0.06]">
          <button onClick={() => setCollapsed(!collapsed)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-300 hover:bg-white/[0.04] transition-colors">
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* User */}
        {!collapsed && (
          <div className="p-3 border-t border-white/[0.06] shrink-0">
            <div className="flex items-center gap-3 px-2 py-1.5">
              <div className="w-8 h-8 rounded-full bg-primary-600/20 border border-primary-500/30 flex items-center justify-center">
                <span className="text-xs font-semibold text-primary-400">{user?.username?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.username}</p>
                <p className="text-xs text-slate-500">Investor</p>
              </div>
              <button onClick={handleLogout} className="p-1.5 text-slate-500 hover:text-red-400 rounded-lg hover:bg-white/[0.04] transition-colors" title="Logout">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 flex items-center justify-between px-4 md:px-6 border-b border-white/[0.06] bg-fintech-900/80 backdrop-blur-xl shrink-0 sticky top-0 z-30">
          <button className="lg:hidden p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.04]" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <Link to="/notifications" className="relative p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.04] transition-colors">
              <Bell className="w-5 h-5" />
            </Link>
            <div className="w-px h-6 bg-white/[0.06] mx-1 hidden sm:block" />
            <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors cursor-default">
              <div className="w-8 h-8 rounded-full bg-primary-600/20 border border-primary-500/30 flex items-center justify-center">
                <span className="text-xs font-semibold text-primary-400">{user?.username?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <span className="text-sm font-medium text-slate-200 hidden sm:block">{user?.username}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
